
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { usePollFetch } from "@/hooks/polls/usePollFetch";
import { useVotePermissions } from "@/hooks/polls/useVotePermissions";
import PollsFilters, { PollFilters } from "@/components/polls/PollsFilters";
import PollsList from "@/components/polls/PollsList";
import { useFilteredPolls } from "@/hooks/polls/useFilteredPolls";
import { useUserVotes } from "@/hooks/polls/useUserVotes";
import { Poll } from "@/types/poll";
import { InfoIcon } from "lucide-react";

const DashboardPolls = () => {
  const { user, profile } = useAuth();
  const { canVote } = useVotePermissions();
  const { userVotes } = useUserVotes();
  
  const [filters, setFilters] = useState<PollFilters>({
    status: 'active',
    country: profile?.country_code || null,
    constituency: profile?.constituency_code || null,
    category: null,
    year: null, // Add the year property
    search: '',
  });

  const { polls, isLoading } = usePollFetch({
    includeAllStatuses: true,
    countryCode: profile?.country_code
  });

  // Filter polls that the user is eligible for
  const eligiblePolls = polls.filter(poll => {
    // Check if user has already voted on this poll
    const hasVoted = userVotes[poll.id] !== undefined;
    
    // Skip polls the user has already voted on (unless explicitly showing all)
    if (hasVoted && filters.status === 'active') return false;
    
    // Check country match (or global poll)
    const countryMatch = !poll.country_code || poll.country_code === profile?.country_code;
    
    // Check constituency match (or no constituency restriction)
    const constituencyMatch = !poll.constituency_code || poll.constituency_code === profile?.constituency_code;
    
    return countryMatch && constituencyMatch;
  });

  // Apply user-selected filters
  const { filteredPolls, activeFiltersCount } = useFilteredPolls({
    polls: eligiblePolls,
    status: filters.status,
    countryCode: filters.country,
    constituencyCode: filters.constituency,
    category: filters.category,
    year: filters.year, // Add the year property
    search: filters.search,
  });

  // Filter for completed polls (user has voted)
  const completedPolls = polls.filter(poll => {
    const hasVoted = userVotes[poll.id] !== undefined;
    return hasVoted;
  });

  const handleFilterChange = (newFilters: PollFilters) => {
    setFilters(newFilters);
  };

  useEffect(() => {
    document.title = "My Polls | NationCard™";
  }, []);

  // Custom component to render poll cards with voted status
  const PollsterPollsList = ({ polls }: { polls: Poll[] }) => {
    if (polls.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No polls available matching your criteria.
        </div>
      );
    }
    
    return (
      <PollsList 
        polls={polls} 
        emptyMessage="No eligible polls found. Check back later for new polls."
      />
    );
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      
      <main className="container mx-auto px-4 py-20 flex-1">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-bold mb-2 text-nation-blue dark:text-blue-300">
            My Polls
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-8">
            Manage and participate in polls available to you as a verified pollster.
          </p>
          
          {!canVote() && (
            <Card className="mb-6 border-amber-200 bg-amber-50 dark:bg-amber-950/20">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <InfoIcon className="text-amber-500 h-5 w-5 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-amber-800 dark:text-amber-400">
                      Verification Required
                    </h3>
                    <p className="text-amber-700 dark:text-amber-500 text-sm">
                      You need to be a verified pollster to participate in polls. Please complete your profile and identity verification.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Available Polls</span>
                {canVote() && (
                  <Badge variant="outline" className="ml-2 text-green-600 border-green-200 bg-green-50 dark:bg-green-950/20">
                    {filteredPolls.length} polls available
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PollsFilters onFilterChange={handleFilterChange} />
              
              {isLoading ? (
                <div className="py-8 text-center">
                  <div className="animate-spin h-8 w-8 border-4 border-nation-blue border-t-transparent rounded-full mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading polls...</p>
                </div>
              ) : (
                <PollsterPollsList polls={filteredPolls} />
              )}
            </CardContent>
          </Card>
          
          {completedPolls.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>
                  <span>Polls You've Completed</span>
                  <Badge variant="outline" className="ml-2 text-blue-600 border-blue-200 bg-blue-50 dark:bg-blue-950/20">
                    {completedPolls.length} polls completed
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PollsList 
                  polls={completedPolls} 
                  emptyMessage="You haven't completed any polls yet."
                />
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default DashboardPolls;
